import React, { useEffect, useState } from "react";
import { toLocalYmd } from "../../utils/dateTime";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import CustomerSelector from "../Selector/CustomerSelector";
import { WarningToast } from "../../utils/ShowToast";
import {
  validateForm,
  defaultValidationRules,
  sanitizeInput,
} from "../../utils/validation";

const EDIT_DRAFT_RULES = {
  quantity: defaultValidationRules.quantity,
  date: defaultValidationRules.date,
};

const getFirstValidationError = (errors) =>
  Object.values(errors || {})
    .flat()
    .find(Boolean);

const EditDraftStockModal = ({ open, setOpen, stockDetail, onSave, products }) => {
  const [invoiceNo, setInvoiceNo] = useState("");
  const [truckNo, setTruckNo] = useState("");
  const [qty, setQty] = useState("");
  const [desc, setDesc] = useState("");
  const [curDate, setCurDate] = useState("");
  const [directSendEnabled, setDirectSendEnabled] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerInvoiceNo, setCustomerInvoiceNo] = useState("");
  const [customerDesc, setCustomerDesc] = useState("");
  const [directQty, setDirectQty] = useState("");
  const [directPrice, setDirectPrice] = useState("");
  const [directPayment, setDirectPayment] = useState("");

  useEffect(() => {
    if (!stockDetail) return;
    setInvoiceNo(stockDetail.invoice_no ?? "");
    setTruckNo(stockDetail.truck_no ?? "");
    setQty(stockDetail.qty ?? "");
    setDesc(stockDetail.desc ?? "");
    setCurDate(
      stockDetail.date
        ? toLocalYmd(stockDetail.date)
        : toLocalYmd()
    );

    const ds = stockDetail.directSend;
    const hasDirect = Boolean(ds && Number(ds.direct_qty) > 0);
    setDirectSendEnabled(hasDirect);
    setSelectedCustomer(
      hasDirect && stockDetail.direct_customer && stockDetail.direct_customer !== "-"
        ? {
            _id: ds.customerId,
            name: stockDetail.direct_customer,
            type: stockDetail.direct_customer_type || 1,
          }
        : ""
    );
    setCustomerInvoiceNo(hasDirect ? ds.invoice_no ?? "" : "");
    setCustomerDesc(hasDirect ? ds.desc ?? "" : "");
    setDirectQty(hasDirect ? ds.direct_qty ?? "" : "");
    setDirectPrice(hasDirect ? ds.price ?? "" : "");
    setDirectPayment(hasDirect ? ds.payment ?? "" : "");
  }, [stockDetail]);

  useEffect(() => {
    if (!directSendEnabled || !stockDetail) return;
    const purchase = Number(stockDetail.purchase || 0);
    if (purchase > 0) {
      setDirectPrice(String(purchase));
    }
  }, [directSendEnabled, stockDetail?.purchase, stockDetail?.itemId]);

  const handleClose = () => setOpen(false);

  const handleSave = () => {
    const validation = validateForm(
      {
        quantity: qty,
        date: curDate,
      },
      EDIT_DRAFT_RULES
    );

    if (!validation.isValid) {
      WarningToast(getFirstValidationError(validation.errors) || "Invalid input");
      return;
    }

    const sanitizedQty = Number(sanitizeInput.number(qty));
    const purchase = Number(stockDetail.purchase || 0);

    if (directSendEnabled) {
      if (!selectedCustomer?._id) {
        WarningToast("Select customer for direct send");
        return;
      }
      if (!String(customerDesc).trim()) {
        WarningToast("Enter customer sale description");
        return;
      }
      if (!directQty || Number(directQty) <= 0) {
        WarningToast("Enter valid direct send quantity");
        return;
      }
      if (Number(directQty) > sanitizedQty) {
        WarningToast("Direct qty cannot exceed total stock qty");
        return;
      }
      if (!purchase || purchase <= 0) {
        WarningToast("Item purchase price is required for direct customer sale");
        return;
      }
    }

    const directSend = directSendEnabled
      ? {
          customerId: selectedCustomer._id,
          itemId: stockDetail.itemId,
          name: stockDetail.item || "",
          unit: stockDetail.unit || "",
          purchase: Number(stockDetail.purchase || 0),
          invoice_no: String(sanitizeInput.text(customerInvoiceNo)).trim(),
          desc: sanitizeInput.text(customerDesc),
          direct_qty: Number(sanitizeInput.number(directQty)),
          price: purchase,
          payment: Number(sanitizeInput.number(directPayment) || 0),
          discount: 0,
        }
      : null;

    const updated = {
      ...stockDetail,
      qty: sanitizedQty,
      purchase,
      total_amount: purchase * sanitizedQty,
      invoice_no: Number(sanitizeInput.text(invoiceNo)),
      truck_no: sanitizeInput.text(truckNo),
      desc: sanitizeInput.text(desc) || "-",
      date: Math.floor(new Date(curDate).getTime() / 1000),
      directSend,
      unit: stockDetail.unit || "",
      direct_customer: directSend ? selectedCustomer.name : "-",
      direct_customer_type: directSend ? selectedCustomer.type : null,
      direct_qty: directSend ? Number(directQty) : "-",
      sale_invoice_no: directSend ? String(customerInvoiceNo) : "-",
    };

    const saved = onSave?.(updated);
    if (saved === false) return;

    setOpen(false);
  };

  if (!stockDetail) return null;

  return (
    <CustomModal open={open} setOpen={setOpen}>
      <div className="pb-8 max-h-[85vh] overflow-y-auto">
        <h1 className="w-full text-center font-[700] text-3xl py-8">
          Edit Stock Line
        </h1>
        <div className="flex flex-col gap-y-4 px-10">
          <div className="text-sm text-inkSecondary">
            <span className="font-semibold text-ink">Item:</span>{" "}
            {stockDetail.item || "—"}
          </div>
          <div className="text-sm text-inkSecondary">
            <span className="font-semibold text-ink">Company:</span>{" "}
            {stockDetail.company || "—"}
          </div>
          <AuthInput
            label="Invoice No"
            placeholder="Enter Invoice No"
            Value={invoiceNo}
            setValue={setInvoiceNo}
            Type="number"
          />
          <AuthInput
            label="Truck No"
            placeholder="Enter Truck No"
            Value={truckNo}
            setValue={setTruckNo}
          />
          <AuthInput
            label="Quantity"
            placeholder="Enter Quantity"
            Type="number"
            Value={qty}
            setValue={setQty}
          />
          <AuthInput
            label="Description"
            placeholder="Enter Description"
            Value={desc}
            setValue={setDesc}
          />
          <AuthInput
            label="Date"
            placeholder="Select Date"
            Type="date"
            Value={curDate}
            setValue={setCurDate}
          />

          <label className="flex items-center gap-2 cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={directSendEnabled}
              onChange={(e) => setDirectSendEnabled(e.target.checked)}
              className="w-4 h-4 accent-main"
            />
            <span className="text-sm font-semibold text-ink">
              Direct customer ko bhej di
            </span>
          </label>

          {directSendEnabled && (
            <div className="flex flex-col gap-y-4 border border-main/30 rounded-xl p-4 bg-brandSoft">
              <CustomerSelector
                SelectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
              <AuthInput
                label="Customer Sale Invoice No (optional)"
                Named="SaleBill"
                placeholder="Optional — auto # assigned"
                Value={customerInvoiceNo}
                setValue={setCustomerInvoiceNo}
                Type="number"
              />
              <AuthInput
                label="Customer Sale Description"
                placeholder="Enter sale description"
                Value={customerDesc}
                setValue={setCustomerDesc}
              />
              <AuthInput
                label={`Direct Qty (max ${qty || "—"})`}
                placeholder="Kitna direct customer ko"
                Value={directQty}
                setValue={setDirectQty}
                Type="number"
              />
              <AuthInput
                label="Sale Unit Price (purchase rate)"
                placeholder="Purchase price"
                Value={directPrice}
                setValue={() => {}}
                Type="number"
                disabled
              />
              <AuthInput
                label="Payment (optional)"
                placeholder="Cash received"
                Value={directPayment}
                setValue={setDirectPayment}
                Type="number"
              />
            </div>
          )}
        </div>
        <div className="flex justify-center items-center gap-x-3 mt-5 px-5">
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 h-11 border-2 border-main bg-main hover:bg-mainHover text-white rounded-lg text-base font-bold transition-colors active:scale-[.98]"
          >
            Save
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 h-11 border-2 border-line bg-card hover:bg-subtle text-inkSecondary rounded-lg text-base font-bold transition-colors active:scale-[.98]"
          >
            Cancel
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditDraftStockModal;
